import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  ObservabilitySetupMode,
  useWaitForFirstRequestOrTrace,
} from "./useWaitForFirstRequestOrTrace";

import { generateNewApiKey } from "@/api/application-api";
import { LanguageType } from "@/components/CodeEditor/types";
import { useAuth } from "@/context/auth-context";
import { useOnboardingFlow } from "@/context/onboarding-flow-context";
import { useOnboardingSidebar } from "@/context/onboarding-sidebar-context";
import {
  USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS,
  useUserOnboarding,
} from "@/queries/onboarding";
import { useOnboardingWorkspaceId } from "@/routes/onboarding/hooks/useOnboardingWorkspaceId";
import { getOnboardingDestinationUrl } from "@/routes/onboarding/utils";
import { useRefreshPromptLayerApiKeys } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import {
  TRACE_PROVIDERS,
  TraceProviderId,
} from "@/routes/onboarding/ObservabilitySetup/constants/traceProviders";

export const useObservabilitySetup = () => {
  const [setupMode, setSetupMode] = useState<ObservabilitySetupMode>("requests");
  const [selectedProviderId, setSelectedProviderId] = useState<TraceProviderId>("promptlayer-sdk");
  const [keyName, setKeyName] = useState("");
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const completionInProgressRef = useRef(false);
  const [language, setLanguage] = useState<LanguageType>("PYTHON");
  const [isCompleted, setIsCompleted] = useState(false);

  const auth = useAuth()!;
  const userToken = auth.userToken!;
  const fallbackWorkspaceId = useOnboardingWorkspaceId();
  const refreshPromptLayerApiKeys = useRefreshPromptLayerApiKeys();
  const { setSidebarContent } = useOnboardingSidebar();
  const { onboardingDestination, clearOnboardingFlow } = useOnboardingFlow();
  const workspaceId = onboardingDestination?.workspaceId ?? fallbackWorkspaceId;

  const { received, itemId, statusLabel } = useWaitForFirstRequestOrTrace({
    mode: setupMode,
    enabled: !isCompleted,
    userToken,
    workspaceId,
  });

  useEffect(() => {
    setSidebarContent(null, "observability-setup");
  }, [setSidebarContent]);

  const { mutateAsync: apiSubmitUserOnboarding, isPending: isSubmitLoading } =
    useUserOnboarding(userToken);
  const { mutateAsync: apiSkipUserOnboarding, isPending: isSkipLoading } =
    useUserOnboarding(userToken);

  const completeOnboarding = useCallback(
    (destinationUrl: string) => {
      setIsCompleted(true);
      clearOnboardingFlow();
      window.location.replace(destinationUrl);
    },
    [clearOnboardingFlow],
  );

  const handleGenerateApiKey = async () => {
    if (isGeneratingKey || apiKey) {
      return;
    }

    setIsGeneratingKey(true);

    try {
      const response = await generateNewApiKey(auth, workspaceId, keyName.trim() || undefined);

      if (response.status !== 200) {
        displayErrorToast("Failed to generate API key");
        return;
      }

      const data = await response.json();
      if (!data.success) {
        displayErrorToast("Failed to generate API key");
        return;
      }

      setApiKey(data.api_key);
      refreshPromptLayerApiKeys();
    } catch {
      displayErrorToast("Failed to generate API key");
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const onGoToItem = useCallback(async () => {
    if (!received || !itemId || completionInProgressRef.current) {
      return;
    }

    completionInProgressRef.current = true;
    setIsCompletingOnboarding(true);
    try {
      const response = await apiSubmitUserOnboarding({
        onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE,
        response: { use_case: "observability" },
      });

      if (response.success) {
        posthog.capture("Onboarding Observability Completed");
        posthog.people.set({ "Onboarding Completed": true });

        const destinationUrl =
          setupMode === "requests"
            ? `/workspace/${workspaceId}/request/${itemId}`
            : `/workspace/${workspaceId}/traces/${itemId}`;
        completeOnboarding(destinationUrl);
      }
    } finally {
      completionInProgressRef.current = false;
      setIsCompletingOnboarding(false);
    }
  }, [apiSubmitUserOnboarding, completeOnboarding, itemId, received, setupMode, workspaceId]);

  const onSkip = useCallback(async () => {
    if (completionInProgressRef.current) {
      return;
    }

    completionInProgressRef.current = true;
    setIsCompletingOnboarding(true);
    try {
      const destinationUrl = await getOnboardingDestinationUrl({
        workspaceId,
        promptTemplateId: onboardingDestination?.promptTemplateId,
        userToken,
      });
      if (!destinationUrl) {
        displayErrorToast("We couldn't open your workspace. Please try again.");
        return;
      }

      const response = await apiSkipUserOnboarding({
        onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE,
        response: { use_case: "observability_skipped" },
      });

      if (response.success) {
        posthog.capture("Onboarding Observability Skipped");
        posthog.people.set({ "Onboarding Completed": true });
        completeOnboarding(destinationUrl);
      }
    } finally {
      completionInProgressRef.current = false;
      setIsCompletingOnboarding(false);
    }
  }, [
    apiSkipUserOnboarding,
    completeOnboarding,
    onboardingDestination?.promptTemplateId,
    userToken,
    workspaceId,
  ]);

  const onFinish = useCallback(async () => {
    if (completionInProgressRef.current) {
      return;
    }

    completionInProgressRef.current = true;
    setIsCompletingOnboarding(true);
    try {
      const destinationUrl = await getOnboardingDestinationUrl({
        workspaceId,
        promptTemplateId: onboardingDestination?.promptTemplateId,
        userToken,
      });
      if (!destinationUrl) {
        displayErrorToast("We couldn't open your workspace. Please try again.");
        return;
      }

      const response = await apiSubmitUserOnboarding({
        onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE,
        response: { use_case: "observability" },
      });

      if (response.success) {
        posthog.capture("Onboarding Observability Completed");
        posthog.people.set({ "Onboarding Completed": true });
        completeOnboarding(destinationUrl);
      }
    } finally {
      completionInProgressRef.current = false;
      setIsCompletingOnboarding(false);
    }
  }, [
    apiSubmitUserOnboarding,
    completeOnboarding,
    onboardingDestination?.promptTemplateId,
    userToken,
    workspaceId,
  ]);

  const selectedProvider =
    TRACE_PROVIDERS.find(provider => provider.id === selectedProviderId) ?? TRACE_PROVIDERS[0];

  const selectProvider = useCallback(
    (providerId: TraceProviderId) => {
      const provider = TRACE_PROVIDERS.find(item => item.id === providerId) ?? TRACE_PROVIDERS[0];

      setSelectedProviderId(providerId);

      if (!provider.languages.includes(language)) {
        setLanguage(provider.languages[0]);
      }
    },
    [language],
  );

  return {
    setupMode,
    setSetupMode,
    selectedProviderId,
    setSelectedProviderId: selectProvider,
    keyName,
    setKeyName,
    apiKey,
    isGeneratingKey,
    language,
    setLanguage,
    received,
    statusLabel,
    selectedProvider,
    isSubmitLoading: isSubmitLoading || isSkipLoading || isCompletingOnboarding,
    isSkipLoading: isSubmitLoading || isSkipLoading || isCompletingOnboarding,
    isCompleted,
    handleGenerateApiKey,
    onSkip,
    onGoToItem,
    onFinish,
  };
};
